import { useEffect, useMemo, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { KbvPluginService } from "@/services/kbvPluginService";
import MatchRow from "@/components/KbvKetQuaLightV32/MatchRow";
import { TZ } from "@/components/KbvKetQuaLightV32/model";
import arrowUp from "../assets/arrow-up.svg";

dayjs.extend(utc);
dayjs.extend(timezone);

function flattenMatches(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) {
    if (payload.length && payload[0]?.matches) {
      return payload.flatMap((group) => (Array.isArray(group.matches) ? group.matches : []));
    }
    return payload;
  }
  if (Array.isArray(payload.data)) return flattenMatches(payload.data);
  return [];
}

function CountMenu({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const options = [5, 10];

  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (event) => {
      if (!rootRef.current?.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("touchstart", onDoc);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("touchstart", onDoc);
    };
  }, [open]);

  return (
    <div className="kbv-home-center__hot-count-wrap" ref={rootRef}>
      <button
        type="button"
        className="kbv-home-center__hot-count"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((next) => !next)}
      >
        <span>{value} trận</span>
        <img
          src={arrowUp}
          alt=""
          width={14}
          height={14}
          style={{ transform: open ? "none" : "rotate(180deg)", width: 14, height: 14 }}
        />
      </button>
      {open ? (
        <div className="kbv-home-center__hot-menu" role="listbox">
          {options.map((option) => (
            <button
              key={option}
              type="button"
              className={`kbv-home-center__hot-option${option === value ? " is-active" : ""}`}
              onClick={() => {
                onChange(option);
                setOpen(false);
              }}
            >
              {option} trận
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}

export default function HotPicks() {
  const [expanded, setExpanded] = useState(true);
  const [count, setCount] = useState(5);
  const [loading, setLoading] = useState(true);
  const [matches, setMatches] = useState([]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const toDate = dayjs().tz(TZ).add(15, "day").format("YYYY-MM-DD");
    KbvPluginService.fetchBangKeoMatchesOld({
      filterInclude: `hotTeam:1,toDate:${toDate}`,
      include: "homeTeam,awayTeam,odd",
      status: "prepare",
      page: 1,
      limit: 10,
    })
      .then((result) => {
        if (cancelled) return;
        const rows = flattenMatches(result?.data)
          .slice()
          .sort((a, b) => (Number(a?.matchTime) || 0) - (Number(b?.matchTime) || 0));
        setMatches(rows);
      })
      .catch(() => {
        if (!cancelled) setMatches([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const visible = useMemo(() => matches.slice(0, count), [matches, count]);

  if (!loading && matches.length === 0) return null;

  return (
    <div className="kbv-home-center__hot">
      <div className="kbv-home-center__hot-head">
        <div className="kbv-home-center__hot-title">
          <span>Trận cầu tâm điểm</span>
          <CountMenu value={count} onChange={setCount} />
        </div>
        <button
          type="button"
          className={`kbv-home-center__hot-toggle${expanded ? "" : " is-collapsed"}`}
          aria-label={expanded ? "Thu gọn" : "Mở rộng"}
          onClick={() => setExpanded((value) => !value)}
        >
          <img src={arrowUp} alt="" />
        </button>
      </div>
      {expanded ? (
        <div className="kbv-home-center__hot-body">
          {loading ? (
            <div className="kbv-home-center__spinner">
              <div className="kbv-home-center__spinner-ring" />
            </div>
          ) : (
            <div className="kbv-kq-v32">
              {visible.map((match, index) => (
                <MatchRow
                  key={match?.id ?? index}
                  match={match}
                  isLast={index === visible.length - 1}
                />
              ))}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
